import { xAccessor, yAccessor } from './line-accessors.js';
import { LineCanvas } from '../../../core/components/canvas/components/LineCanvas.js';
import { shouldDisplayPoints } from '../../../core/components/canvas/utils/display-points.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { POINT_OVERLAY_PADDING } from '../../../core/components/data-point/constants.js';
import { DEFAULT_FILL_OPACITY } from '../../../core/constants.js';
import { isIsolatedPoint } from '../../../core/utils/is-isolated-point.js';
import { DEFAULT_POINTS_DISPLAY } from '../../constants/defaults.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  LineMetadata,
  LineSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import type { LineDatapoint } from '../../types/xy-chart.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { isGapDatapointForDisplay } from '../../utils/validation-datapoint.js';
import { relAccessor } from '../../utils/value-representation.js';

interface LineDisplayProps {
  axisIdToScales: AxisIdToScales;
  series: LineSeriesWithDatapoints;
  metadata: LineMetadata;
}

export function LineDisplay({
  axisIdToScales,
  series,
  metadata,
}: LineDisplayProps) {
  const { height, width } = useGridPlotSize();
  const { datapointMetadataMap, axisIdToScaleDomain } = useXYChartData();

  const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
  const { xAxis, yAxis } = metadata;
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const { curve, pointsDisplay, color } = metadata;
  const showDataPoints = shouldDisplayPoints(
    pointsDisplay ?? DEFAULT_POINTS_DISPLAY,
    width,
    series.data.length,
  );

  const isolatedAccessor = (datapoints: LineDatapoint[], index: number) =>
    showDataPoints ||
    isIsolatedPoint(datapoints, index, (point) =>
      isGapDatapointForDisplay(datapointMetadataMap)(point),
    );

  return (
    <LineCanvas
      key={series.id}
      data={series.data}
      canvasHeight={height}
      canvasWidth={width}
      opacity={DEFAULT_FILL_OPACITY}
      color={() => color.Default}
      x={xAccessor(xScale, xRelAccessor)}
      y={yAccessor(yScale, yRelAccessor)}
      enableDotOverflow
      displayScatterPlot={isolatedAccessor}
      overlayPadding={POINT_OVERLAY_PADDING}
      curve={curve}
      defined={isGapDatapointForDisplay(datapointMetadataMap)}
    />
  );
}

LineDisplay['displayName'] = 'LineDisplay';
